import { Fragment, useState } from "react";
import {
  Transition,
  Dialog,
} from "@headlessui/react";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import ButtonThird from "shared/Button/ButtonThird";
import Checkbox from "shared/Checkbox/Checkbox";
import Slider from "rc-slider";
import { FunnelIcon } from "@heroicons/react/24/outline";

const TabFilters = ({
  types,
  makes,
  models,
  selectedTypes,
  selectedMakes,
  selectedModels,
  setSelectedTypes,
  setSelectedMakes,
  setSelectedModels,
  rangePrices,
  setRangePrices,
  maxPrice,
  sortOrder,
  setSortOrder,
  showMakeFilter = true,
  showModelFilter = true,
}: {
  types: string[];
  makes: string[];
  models: string[];
  selectedTypes: string[];
  selectedMakes: string[];
  selectedModels: string[];
  setSelectedTypes: (types: string[]) => void;
  setSelectedMakes: (makes: string[]) => void;
  setSelectedModels: (models: string[]) => void;
  rangePrices: number[];
  setRangePrices: (prices: number[]) => void;
  maxPrice: number;
  sortOrder: string;
  setSortOrder: (order: string) => void;
  showMakeFilter?: boolean;
  showModelFilter?: boolean;
}) => {
  const [selectedAllTypes, setSelectedAllTypes] = useState<boolean>(false);
  const [selectedAllMakes, setSelectedAllMakes] = useState<boolean>(false);
  const [selectedAllModels, setSelectedAllModels] = useState<boolean>(false);
  const [tempTypes, setTempTypes] = useState<string[]>(selectedTypes);
  const [tempMakes, setTempMakes] = useState<string[]>(selectedMakes);
  const [tempModels, setTempModels] = useState<string[]>(selectedModels);
  const [tempAllTypes, setTempAllTypes] = useState<boolean>(selectedAllTypes);
  const [tempAllMakes, setTempAllMakes] = useState<boolean>(selectedAllMakes);
  const [tempAllModels, setTempAllModels] = useState<boolean>(selectedAllModels);
  const [tempRangePrices, setTempRangePrices] = useState<number[]>(rangePrices);
  const [tempSortOrder, setTempSortOrder] = useState<string>(sortOrder);
  const [mobileOpen, setMobileOpen] = useState(false);

  const customSort = (a: string, b: string) => {
    const isANumeric = /^\d/.test(a);
    const isBNumeric = /^\d/.test(b);
    const isASpecial = /^[^a-zA-Z0-9]/.test(a);
    const isBSpecial = /^[^a-zA-Z0-9]/.test(b);

    if (isASpecial && !isBSpecial) return 1;
    if (!isASpecial && isBSpecial) return -1;
    if (isASpecial && isBSpecial) return a.localeCompare(b);

    if (isANumeric && !isBNumeric) return 1;
    if (!isANumeric && isBNumeric) return -1;
    return a.localeCompare(b, undefined, { numeric: true });
  };

  const handleClearFilters = () => {
    setSelectedAllTypes(false);
    setSelectedTypes([]);
    setSelectedAllMakes(false);
    setSelectedMakes([]);
    setSelectedAllModels(false);
    setSelectedModels([]);
    setRangePrices([0, maxPrice]);
    setSortOrder("default");
    setTempAllTypes(false);
    setTempTypes([]);
    setTempAllMakes(false);
    setTempMakes([]);
    setTempAllModels(false);
    setTempModels([]);
    setTempRangePrices([0, maxPrice]);
    setTempSortOrder("default");
    setMobileOpen(false);
  };

  const initTempFilters = () => {
    setTempTypes(selectedTypes);
    setTempMakes(selectedMakes);
    setTempModels(selectedModels);
    setTempAllTypes(selectedAllTypes);
    setTempAllMakes(selectedAllMakes);
    setTempAllModels(selectedAllModels);
    setTempRangePrices(rangePrices);
    setTempSortOrder(sortOrder);
  };

  const renderMobilePanel = () => (
    <Dialog
      open={mobileOpen}
      onClose={() => setMobileOpen(false)}
      className="fixed inset-0 z-50 flex items-center justify-end"
    >
      <Transition
        appear
        show={mobileOpen}
        as={Fragment}
        enter="transition-opacity duration-200"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-150"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div
          className="fixed inset-0 bg-black bg-opacity-40"
          onClick={() => setMobileOpen(false)}
        />
      </Transition>

      <Transition
        appear
        show={mobileOpen}
        as={Fragment}
        enter="transform transition-transform duration-300 ease-out"
        enterFrom="translate-x-full"
        enterTo="translate-x-0"
        leave="transform transition-transform duration-200 ease-in"
        leaveFrom="translate-x-0"
        leaveTo="translate-x-full"
      >
        <div
          className="
            relative bg-bg-color h-full w-full sm:w-[22rem] lg:w-2xl
            shadow-xl !rounded-l-lg rounded-r-none flex flex-col overflow-y-auto px-4
          "
        >
          <div className="flex items-center justify-between px-6 py-4 border-b border-secondary-100">
            <span className="text-lg font-semibold text-text-color">Filters</span>
            <button
              className="p-2 rounded-full"
              onClick={() => setMobileOpen(false)}
              aria-label="Close"
            >
              <span className="text-2xl">&times;</span>
            </button>
          </div>
          <div className="flex flex-col gap-6 px-6 py-4 overflow-y-auto">
            {/* Category */}
            {types.length > 0 && (
              <div>
                <div className="font-semibold mb-2 text-text-color">Category</div>
                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                  <Checkbox
                    name="Select All"
                    label="Select All"
                    defaultChecked={tempAllTypes}
                    onChange={(checked) => {
                      setTempAllTypes(checked);
                      setTempTypes(checked ? types : []);
                    }}
                    truncate={true}
                  />
                  {types.map((type) => (
                    <Checkbox
                      key={type}
                      name={type}
                      label={type}
                      title={type}
                      defaultChecked={tempTypes.includes(type)}
                      onChange={(checked) => {
                        if (checked) {
                          const next = [...tempTypes, type];
                          setTempTypes(next);
                          if (next.length === types.length) setTempAllTypes(true);
                        } else {
                          setTempTypes(tempTypes.filter((t) => t !== type));
                          setTempAllTypes(false);
                        }
                      }}
                      truncate={true}
                    />
                  ))}
                </div>
              </div>
            )}
            {/* Make */}
            {showMakeFilter && makes.length > 0 && (
              <div>
                <div className="font-semibold mb-2 text-text-color">Make</div>
                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                  <Checkbox
                    name="Select All Makes"
                    label="Select All"
                    defaultChecked={tempAllMakes}
                    onChange={(checked) => {
                      setTempAllMakes(checked);
                      setTempMakes(checked ? makes : []);
                    }}
                    truncate={true}
                  />
                  {[...makes].sort(customSort).map((make) => (
                    <Checkbox
                      key={make}
                      name={make}
                      label={make}
                      title={make}
                      defaultChecked={tempMakes.includes(make)}
                      onChange={(checked) => {
                        if (checked) {
                          const next = [...tempMakes, make];
                          setTempMakes(next);
                          if (next.length === makes.length) setTempAllMakes(true);
                        } else {
                          setTempMakes(tempMakes.filter((m) => m !== make));
                          setTempAllMakes(false);
                        }
                      }}
                      truncate={true}
                    />
                  ))}
                </div>
              </div>
            )}
            {/* Model */}
            {showModelFilter && models.length > 0 && (
              <div>
                <div className="font-semibold mb-2 text-text-color">Model</div>
                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                  <Checkbox
                    name="Select All Models"
                    label="Select All"
                    defaultChecked={tempAllModels}
                    onChange={(checked) => {
                      setTempAllModels(checked);
                      setTempModels(checked ? models : []);
                    }}
                    truncate={true}
                  />
                  {[...models].sort(customSort).map((model) => (
                    <Checkbox
                      key={model}
                      name={model}
                      label={model}
                      title={model}
                      defaultChecked={tempModels.includes(model)}
                      onChange={(checked) => {
                        if (checked) {
                          const next = [...tempModels, model];
                          setTempModels(next);
                          if (next.length === models.length) setTempAllModels(true);
                        } else {
                          setTempModels(tempModels.filter((m) => m !== model));
                          setTempAllModels(false);
                        }
                      }}
                      truncate={true}
                    />
                  ))}
                </div>
              </div>
            )}
            {/* Price Range */}
            <div>
              <div className="font-semibold mb-2 text-text-color">Price per day</div>
              <div className="space-y-4">
                <Slider
                  range
                  className="text-red-400"
                  min={0}
                  max={maxPrice}
                  value={tempRangePrices}
                  allowCross={false}
                  onChange={(e) => setTempRangePrices(e as number[])}
                />
                <div className="flex justify-between space-x-5">
                  <div>
                    <label htmlFor="minPriceMobile" className="block text-sm font-medium text-text-color ">
                      Min price
                    </label>
                    <div className="mt-1 relative rounded-md">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-text-color sm:text-sm">$</span>
                      </div>
                      <input
                        type="text"
                        id="minPriceMobile"
                        className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-7 pr-3 sm:text-sm border-secondary-100 rounded-md text-text-color"
                        value={tempRangePrices[0]}
                        onChange={(e) => {
                          const v = e.target.value.replace(/[^0-9]/g, "");
                          setTempRangePrices([v === "" ? 0 : parseInt(v, 10), tempRangePrices[1]]);
                        }}
                        onKeyDown={(e) => {
                          if (!/^\d$/.test(e.key) && !["Backspace","Delete","ArrowLeft","ArrowRight","Tab"].includes(e.key)) e.preventDefault();
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="maxPriceMobile" className="block text-sm font-medium text-text-color ">
                      Max price
                    </label>
                    <div className="mt-1 relative rounded-md">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-text-color sm:text-sm">$</span>
                      </div>
                      <input
                        type="text"
                        id="maxPriceMobile"
                        className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-7 pr-3 sm:text-sm border-secondary-100 rounded-md text-text-color"
                        value={tempRangePrices[1]}
                        onChange={(e) => {
                          const v = e.target.value.replace(/[^0-9]/g, "");
                          setTempRangePrices([tempRangePrices[0], v === "" ? 0 : parseInt(v, 10)]);
                        }}
                        onKeyDown={(e) => {
                          if (!/^\d$/.test(e.key) && !["Backspace","Delete","ArrowLeft","ArrowRight","Tab"].includes(e.key)) e.preventDefault();
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Sort */}
            <div>
              <div className="font-semibold mb-2 text-text-color">Sort</div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: "default", name: "Default" },
                  { id: "aToZ", name: "A to Z" },
                  { id: "zToA", name: "Z to A" },
                  { id: "priceLowToHigh", name: "Price: Low to High" },
                  { id: "priceHighToLow", name: "Price: High to Low" },
                ].map((option) => (
                  <Checkbox
                    key={option.id}
                    name={option.id}
                    label={option.name}
                    title={option.name}
                    defaultChecked={tempSortOrder === option.id}
                    onChange={() => setTempSortOrder(option.id)}
                    truncate={true}
                  />
                ))}
              </div>
            </div>
          </div>
          <div className="px-6 py-4 border-t border-secondary-100 flex justify-between gap-2 mt-auto">
            <ButtonThird onClick={handleClearFilters}>Clear</ButtonThird>
            <ButtonPrimary
              onClick={() => {
                setSelectedAllTypes(tempAllTypes);
                setSelectedTypes(tempTypes);
                setSelectedAllMakes(tempAllMakes);
                setSelectedMakes(tempMakes);
                setSelectedAllModels(tempAllModels);
                setSelectedModels(tempModels);
                setRangePrices(tempRangePrices);
                setSortOrder(tempSortOrder);
                setMobileOpen(false);
              }}
            >
              Apply Filters
            </ButtonPrimary>
          </div>
        </div>
      </Transition>
    </Dialog>
  );
  const isFilterApplied =
    selectedTypes.length > 0 ||
    selectedMakes.length > 0 ||
    selectedModels.length > 0 ||
    (rangePrices[0] !== 0 || rangePrices[1] !== maxPrice) ||
    sortOrder !== "default" && sortOrder !== "aToZ";

  return (
    <div className=" mt-4 flex lg:mt-0 lg:gap-4 lg:justify-end">
      <div className="flex  w-full justify-end">
        <button
          className={`p-2 rounded-full border border-secondary-100 bg-bg-color flex items-center ${isFilterApplied ? "bg-primary-700 text-white" : "text-text-color"}`}
          onClick={() => {
            if (!mobileOpen) initTempFilters();
            setMobileOpen(true);
          }}
          aria-label="Open Filters"
        >
          <FunnelIcon className="h-6 w-6 text-text-color" />
        </button>
        {mobileOpen && renderMobilePanel()}
      </div>
    </div>
  );
};

export default TabFilters;
